import { Link } from 'react-router-dom'
import { Cookie, Settings, CheckCircle, XCircle } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

function GlassCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border bg-white/[0.055] border-white/[0.09] backdrop-blur-sm hover:bg-white/[0.08] hover:border-white/[0.14] transition-all duration-300 ${className}`}>
      {children}
    </div>
  )
}

export default function CookiePolicyPage() {
  return (
    <div className="min-h-screen bg-consciousness-deep">
      <SiteHeader />

      <section className="pt-32 pb-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <Cookie className="w-16 h-16 text-trust-blue" strokeWidth={1} />
          </div>
          <h1
            className="text-5xl md:text-7xl tracking-[0.15em] mb-6 text-white"
            style={brandFont}
          >
            Cookie Policy
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto mb-4" style={bodyFont}>
            How we use cookies and similar tracking technologies
          </p>
          <p className="text-sm text-white/40" style={bodyFont}>
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </section>

      <section className="py-8 px-6 pb-24">
        <div className="max-w-5xl mx-auto space-y-8">

          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white" style={{ ...bodyFont, fontWeight: 300 }}>What Are Cookies?</h2>
              <p className="text-white/70 leading-relaxed" style={bodyFont}>
                Cookies are small text files stored on your device when you visit our website. They help us provide a better
                user experience by remembering your preferences and analyzing how you use our Services.
              </p>
            </div>
          </GlassCard>

          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <CheckCircle className="w-6 h-6 text-trust-blue" />
                Essential Cookies
              </h2>
              <p className="text-white/70 leading-relaxed mb-4" style={bodyFont}>
                These cookies are necessary for the website to function and cannot be disabled:
              </p>
              <ul className="space-y-3 text-white/70" style={bodyFont}>
                {[
                  { label: 'Authentication', desc: 'Remember your login session via LiD' },
                  { label: 'Security', desc: 'Protect against CSRF attacks and fraud' },
                  { label: 'Session Management', desc: 'Maintain your preferences during a session' },
                ].map(({ label, desc }) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-verified-green flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-white" style={{ fontWeight: 400 }}>{label}:</strong> {desc}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </GlassCard>

          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <Settings className="w-6 h-6 text-trust-blue" />
                Performance & Analytics Cookies
              </h2>
              <p className="text-white/70 leading-relaxed mb-4" style={bodyFont}>
                These cookies help us understand how visitors use our Services (with your consent):
              </p>
              <ul className="space-y-3 text-white/70" style={bodyFont}>
                {[
                  { label: 'Usage Analytics', desc: 'Track page views, feature usage, and navigation patterns' },
                  { label: 'Performance Monitoring', desc: 'Measure load times and optimize experience' },
                  { label: 'Error Tracking', desc: 'Identify and fix technical issues' },
                ].map(({ label, desc }) => (
                  <li key={label} className="flex items-start gap-3">
                    <Settings className="w-5 h-5 text-trust-blue flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-white" style={{ fontWeight: 400 }}>{label}:</strong> {desc}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </GlassCard>

          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                Functional Cookies
              </h2>
              <p className="text-white/70 leading-relaxed mb-4" style={bodyFont}>
                These cookies enhance functionality and personalization (optional):
              </p>
              <ul className="space-y-3 text-white/70" style={bodyFont}>
                {[
                  { label: 'Language Preferences', desc: 'Remember your language selection' },
                  { label: 'Theme Settings', desc: 'Save dark/light mode preferences' },
                  { label: 'UI Customization', desc: 'Remember dashboard layouts and settings' },
                ].map(({ label, desc }) => (
                  <li key={label} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-lambda-gold flex-shrink-0 mt-1" />
                    <div>
                      <strong className="text-white" style={{ fontWeight: 400 }}>{label}:</strong> {desc}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </GlassCard>

          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <XCircle className="w-6 h-6 text-warning-amber" />
                What We DON&apos;T Do
              </h2>
              <ul className="space-y-3 text-white/70" style={bodyFont}>
                {[
                  'We do not use advertising or marketing cookies',
                  'We do not sell cookie data to third parties',
                  'We do not track you across other websites',
                  'We do not use cookies for behavioral profiling without consent',
                ].map((text) => (
                  <li key={text} className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-error-red flex-shrink-0 mt-1" />
                    <div>{text}</div>
                  </li>
                ))}
              </ul>
            </div>
          </GlassCard>

          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white flex items-center gap-3" style={{ ...bodyFont, fontWeight: 300 }}>
                <Settings className="w-6 h-6 text-trust-blue" />
                Managing Cookie Preferences
              </h2>
              <div className="space-y-4 text-white/70" style={bodyFont}>
                <p className="leading-relaxed">You have control over cookie settings:</p>
                <ul className="space-y-2 list-disc list-inside ml-4">
                  <li>Use our Cookie Preferences Center (in account settings)</li>
                  <li>Configure your browser to block or delete cookies</li>
                  <li>Opt out of analytics cookies while keeping essential ones</li>
                  <li>Clear cookies at any time through browser settings</li>
                </ul>
                <p className="leading-relaxed mt-4">
                  Note: Blocking essential cookies may impact functionality.
                </p>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <div className="p-8">
              <h2 className="text-2xl tracking-wide mb-6 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                Third-Party Services
              </h2>
              <div className="space-y-4 text-white/70" style={bodyFont}>
                <p className="leading-relaxed">We use the following third-party services that may set cookies:</p>
                <ul className="space-y-3">
                  <li><strong className="text-white" style={{ fontWeight: 400 }}>Analytics:</strong> Google Analytics (anonymized IP)</li>
                  <li><strong className="text-white" style={{ fontWeight: 400 }}>Infrastructure:</strong> Cloudflare (security and performance)</li>
                  <li><strong className="text-white" style={{ fontWeight: 400 }}>Authentication:</strong> LiD authentication service</li>
                </ul>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <div className="p-8 text-center">
              <h2 className="text-2xl tracking-wide mb-4 text-white" style={{ ...bodyFont, fontWeight: 300 }}>
                Questions About Cookies?
              </h2>
              <p className="text-white/50 mb-6" style={bodyFont}>
                Contact us for cookie-related inquiries
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="mailto:privacy@lukhas.com"
                  className="px-6 py-2.5 rounded-full bg-trust-blue text-white text-sm tracking-[0.06em] hover:bg-trust-blue/90 transition-all inline-block"
                  style={bodyFont}
                >
                  privacy@lukhas.com
                </a>
                <Link
                  to="/contact"
                  className="px-6 py-2.5 rounded-full border border-trust-blue/40 text-trust-blue text-sm tracking-[0.06em] hover:border-trust-blue/70 transition-all inline-block"
                  style={bodyFont}
                >
                  Contact Form
                </Link>
              </div>
            </div>
          </GlassCard>

        </div>
      </section>

      <SiteFooter
        columns={[
          {
            title: 'Legal',
            links: [
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Cookie Policy', href: '/cookies' },
            ],
          },
          {
            title: 'Platform',
            links: [
              { label: 'Home', href: '/' },
              { label: 'Solutions', href: '/solutions' },
              { label: 'Contact', href: '/contact' },
            ],
          },
          {
            title: 'Ecosystem',
            links: [
              { label: 'Developers', href: 'https://lukhas.dev', external: true },
              { label: 'App Store', href: 'https://lukhas.store', external: true },
            ],
          },
        ]}
      />
    </div>
  )
}
